import React, { useEffect, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronRight,
  FiCreditCard,
  FiShield,
  FiTruck
} from 'react-icons/fi';
import './CheckoutStyles.css';
import CashfreePayment from './CashfreePayment';

const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const ALL_PAYMENT_METHODS = [
  {
    id: 'cashfree',
    name: 'Online Payment',
    subtitle: 'Cards, UPI, Netbanking',
    description: 'Pay securely using Credit/Debit Card, UPI, Netbanking, or Wallets.',
    highlight: 'Instant confirmation'
  },
  {
    id: 'cod',
    name: 'Cash on Delivery',
    subtitle: 'Pay after delivery',
    description: 'Pay when your order is delivered. Additional charges of \u20B940 will be applied.',
    highlight: 'Doorstep payment'
  }
];

const PaymentForm = ({
  onPaymentMethodSelect,
  selectedPaymentMethod,
  onContinue,
  onBack,
  totalAmount,
  itemsPrice = 0,
  shippingPrice = 0,
  taxPrice = 0,
  order,
  onPaymentFailed,
  codAvailable = true
}) => {
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(() => (
    codAvailable
      ? ALL_PAYMENT_METHODS
      : ALL_PAYMENT_METHODS.filter((method) => method.id !== 'cod')
  ));

  useEffect(() => {
    setPaymentMethods(
      codAvailable
        ? ALL_PAYMENT_METHODS
        : ALL_PAYMENT_METHODS.filter((method) => method.id !== 'cod')
    );

    if (!codAvailable && selectedPaymentMethod?.id === 'cod') {
      onPaymentMethodSelect(null);
    }
  }, [codAvailable, selectedPaymentMethod, onPaymentMethodSelect]);

  const renderMethodIcon = (methodId) => {
    if (methodId === 'cod') {
      return <FiTruck />;
    }

    return <FiCreditCard />;
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentError('');
    onPaymentMethodSelect(method);
  };

  const handleContinue = () => {
    setPaymentError('');

    if (selectedPaymentMethod?.id === 'cod') {
      onContinue();
    }
  };

  const handlePaymentSuccess = (data) => {
    setPaymentSuccess(true);
    setPaymentData(data);

    setTimeout(() => {
      onContinue(data);
    }, 2000);
  };

  return (
    <div className="payment-form-container payment-form-container--reference">
      <div className="payment-layout-shell">
        <aside className="payment-sidebar">
          <button
            type="button"
            className="payment-shell-back"
            onClick={onBack}
            disabled={paymentSuccess}
          >
            Back to Address
          </button>

          <div className="payment-sidebar-card">
            <span className="payment-form-kicker">Secure Checkout</span>
            <h2>Select Payment Method</h2>
            <p>
              Choose how you want to complete this order. Your total, selected
              method, and payment flow stay visible the whole time.
            </p>

            {!codAvailable ? (
              <div className="cod-unavailable-notice">
                <FiAlertTriangle className="cod-unavailable-icon" />
                <div>
                  <strong>Cash on Delivery Not Available</strong>
                  <p>Some items in your cart do not support Cash on Delivery. Please use online payment methods.</p>
                </div>
              </div>
            ) : null}

            <div className="payment-method-grid payment-method-grid--stacked">
              {paymentMethods.map((method) => {
                const isSelected = selectedPaymentMethod?.id === method.id;

                return (
                  <button
                    type="button"
                    key={method.id}
                    className={`payment-method-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handlePaymentMethodChange(method)}
                    aria-pressed={isSelected}
                  >
                    <span className={`payment-icon-shell ${method.id === 'cod' ? 'payment-icon-shell--cod' : ''}`}>
                      {renderMethodIcon(method.id)}
                    </span>

                    <span className="payment-info">
                      <span className="payment-method-subtitle">{method.subtitle}</span>
                      <span className="payment-name">{method.name}</span>
                      <span className="payment-description">{method.description}</span>
                      <span className="payment-method-highlight">{method.highlight}</span>
                    </span>

                    <span className={`payment-method-check ${isSelected ? 'selected' : ''}`}>
                      {isSelected ? <FiCheckCircle /> : <FiChevronRight />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="payment-sidebar-card payment-sidebar-card--summary">
            <div className="payment-summary-card-header">
              <div>
                <h3>Order Summary</h3>
                <p>Quick payment overview before you continue.</p>
              </div>
              <span className="payment-summary-method-pill">
                {selectedPaymentMethod ? selectedPaymentMethod.name : 'Select method'}
              </span>
            </div>

            <div className="payment-summary-list">
              <div className="payment-summary-row">
                <span>Items total</span>
                <strong>{formatCurrency(itemsPrice || totalAmount)}</strong>
              </div>
              <div className="payment-summary-row">
                <span>Delivery</span>
                <strong>{formatCurrency(shippingPrice)}</strong>
              </div>
              {taxPrice > 0 ? (
                <div className="payment-summary-row">
                  <span>Taxes</span>
                  <strong>{formatCurrency(taxPrice)}</strong>
                </div>
              ) : null}
              <div className="payment-summary-total">
                <span>Amount to pay</span>
                <strong>{formatCurrency(totalAmount)}</strong>
              </div>
            </div>

            <div className="payment-summary-note">
              <FiShield />
              <p>Protected checkout with clear totals and a guided payment flow.</p>
            </div>
          </div>
        </aside>

        <section className="payment-main-panel">
          {selectedPaymentMethod?.id === 'cashfree' ? (
            order ? (
              <CashfreePayment
                totalAmount={totalAmount}
                itemsPrice={itemsPrice}
                shippingPrice={shippingPrice}
                taxPrice={taxPrice}
                order={order}
                selectedPaymentMethod={selectedPaymentMethod}
                onSuccess={(paymentPayload) => {
                  handlePaymentSuccess(paymentPayload);
                }}
                onError={(errorPayload) => {
                  const paymentState = typeof errorPayload === 'object' && errorPayload?.paymentState
                    ? errorPayload.paymentState
                    : 'failed';
                  const errorMessage = typeof errorPayload === 'object'
                    ? errorPayload?.message
                    : errorPayload;

                  setPaymentError(
                    errorMessage ||
                    (paymentState === 'incomplete'
                      ? 'Payment not completed. Please proceed again.'
                      : 'Payment failed. Please try again.')
                  );

                  if (onPaymentFailed) {
                    onPaymentFailed({
                      paymentState,
                      message: errorMessage
                    });
                  }
                }}
                onBack={() => onPaymentMethodSelect(null)}
              />
            ) : (
              <div className="payment-stage-panel payment-stage-panel--empty">
                <div className="payment-stage-intro">
                  <span className="payment-stage-badge">Online Checkout</span>
                  <h3>Preparing your payment</h3>
                  <p>Your order is being created. Please wait a moment and try again if it does not appear.</p>
                </div>
                <div className="payment-error-message">
                  <p><strong>Error:</strong> Order not found. Please try again.</p>
                </div>
              </div>
            )
          ) : (
            <div className={`payment-stage-panel ${selectedPaymentMethod?.id === 'cod' ? 'payment-stage-panel--cod' : 'payment-stage-panel--empty'}`}>
              <div className="payment-stage-intro">
                <span className={`payment-stage-badge ${selectedPaymentMethod?.id === 'cod' ? 'payment-stage-badge--success' : ''}`}>
                  {selectedPaymentMethod?.id === 'cod' ? 'Cash on Delivery' : 'Payment'}
                </span>
                <h3>
                  {selectedPaymentMethod?.id === 'cod'
                    ? 'Review before placing your order'
                    : 'Choose a payment method to continue'}
                </h3>
                <p>
                  {selectedPaymentMethod?.id === 'cod'
                    ? 'Cash on Delivery keeps checkout simple. Review the details below and place your order when ready.'
                    : 'Select a payment method from the left panel to unlock the next step and continue securely.'}
                </p>
              </div>

              {selectedPaymentMethod?.id === 'cod' ? (
                <>
                  <div className="payment-stage-grid">
                    <div className="payment-stage-detail-card payment-stage-detail-card--primary">
                      <span className="payment-icon-shell payment-icon-shell--cod">
                        <FiTruck />
                      </span>
                      <div>
                        <span className="payment-stage-label">Payment at doorstep</span>
                        <h4>Cash on Delivery</h4>
                        <p>Pay when your order reaches you. Keep the exact amount ready for a smoother delivery experience.</p>
                      </div>
                    </div>

                    <div className="payment-stage-detail-card">
                      <span className="payment-stage-label">Additional charge</span>
                      <strong className="payment-stage-amount">{formatCurrency(40)}</strong>
                      <div className="payment-stage-mini-list">
                        <div className="payment-stage-mini-row">
                          <span>Payment status</span>
                          <strong>Due on delivery</strong>
                        </div>
                        <div className="payment-stage-mini-row">
                          <span>Selected method</span>
                          <strong>Cash on Delivery</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="payment-stage-note">
                    <FiShield />
                    <p>Your order total is already calculated on the left. Once placed, you can track it from your account.</p>
                  </div>
                </>
              ) : (
                <div className="payment-stage-empty">
                  <div className="payment-stage-empty-card">
                    <FiCreditCard />
                    <div>
                      <strong>Ready when you are</strong>
                      <p>Pick a payment option to see the final payment panel and complete checkout.</p>
                    </div>
                  </div>
                  <div className="payment-stage-empty-card">
                    <FiShield />
                    <div>
                      <strong>Same colors, cleaner flow</strong>
                      <p>The layout is simplified so totals, actions, and important payment info are easier to follow.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="payment-stage-actions">
                <button
                  className="back-button"
                  onClick={onBack}
                  disabled={paymentSuccess}
                >
                  Back to Address
                </button>
                <button
                  className="continue-button"
                  onClick={handleContinue}
                  disabled={!selectedPaymentMethod || selectedPaymentMethod.id !== 'cod' || paymentSuccess}
                >
                  {selectedPaymentMethod?.id === 'cod' ? 'Place Order' : 'Select a method first'}
                </button>
              </div>
            </div>
          )}

          {paymentError ? (
            <div className="payment-error-message">
              <p><strong>Payment Error:</strong> {paymentError}</p>
              <p>Please check your payment details and try again.</p>
            </div>
          ) : null}
        </section>
      </div>

      {paymentSuccess ? (
        <div className="payment-success-overlay">
          <div className="payment-success-content">
            <div className="success-icon"><FiCheckCircle /></div>
            <h3>Payment Successful!</h3>
            <p>Your payment has been processed successfully.</p>
            <p>Order ID: {paymentData?.orderId || paymentData?._id || 'N/A'}</p>
            <p>Payment ID: {paymentData?.paymentId || paymentData?.paymentResult?.id || 'N/A'}</p>
            <p className="payment-note">Redirecting to order confirmation...</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PaymentForm;
